import { FC, createContext, useContext, useCallback, useEffect } from 'react';

import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import {
  useParseFiltersRequestSearchPartnerCompanies,
  useSelectionIds,
  useToggle,
  useDataTableRequest,
  useToggleDialogConfirmation,
  useSubscriptionFeatures,
  useInterval,
} from '~hooks/index';
import { useSignalRContext } from '~contexts/SignalRContext';
import { useClientOnMethod } from '~hooks/useSignalR';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { searchPartnerItemsV2 } from '~services/partnerCompany';
import {
  PartnerCompanyItemFilterV2Base,
  PartnerCompanyItem,
} from '~services/partnerCompany/types';
import { sortOrderPendingByExpirationDate } from '~utils/order';
import { getLimitPremiunByVersion } from '~utils/feature';
import { METHODS_NAME } from '~constants/signalR';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
import { useDashboardVariantV2CarrierCollectFiltersContext } from '../../../CollectFiltersContext';

import {
  DashboardVariantV2CarrierContentCollectTabContextValue,
  DashboardVariantV2CarrierContentCollectTabDialogDetailsOrderData,
} from './types';
import {
  ITEM_KEY_ID,
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_COLLECT_ITEM_STATES,
} from './utils';

const DEFAULT_INTERVAL_RELOAD_ZONE_SATURATION = 15000; //@TODO: UI V2: Verify

const DashboardVariantV2CarrierContentCollectTabContext =
  createContext<DashboardVariantV2CarrierContentCollectTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    selection: {
      selectedIds: [],
      utils: {
        add: () => {},
        addMultiple: () => {},
        toggle: () => {},
        deleteOne: () => {},
        deleteMultiple: () => {},
        deleteCurrentAndAfter: () => {},
        has: () => false,
        isEmpty: () => true,
        reset: () => {},
      },
    },
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    showTracking: {
      value: false,
      toggleValue: () => {},
    },
    isOnlyTodayDelivery: {
      value: false,
      toggleValue: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      confirmValidateOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      cancelOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2CarrierContentCollectTabContext =
  (): DashboardVariantV2CarrierContentCollectTabContextValue =>
    useContext(DashboardVariantV2CarrierContentCollectTabContext);

const DashboardVariantV2CarrierContentCollectTabProvider: FC = ({
  children,
}) => {
  const { hubConnection } = useSignalRContext();

  const { hasAccessToFeature } = useSubscriptionFeatures();

  const {
    data: {
      zoneSaturation: { reload: reloadZoneSaturation },
    },
  } = useDashboardVariantV2CarrierContext();
  const { filters, forceRequest } =
    useDashboardVariantV2CarrierCollectFiltersContext();

  const parseFiltersRequest = useParseFiltersRequestSearchPartnerCompanies({
    filters: filters.filters,
    defaultCollectItemState: DEFAULT_COLLECT_ITEM_STATES,
  });

  const [showTracking, toggleShowTracking] = useToggle(
    hasAccessToFeature(
      PlanFeaturesTypes.LimitPremium,
      getLimitPremiunByVersion('1.24.0'),
    ),
  );
  const [isOnlyTodayDelivery, toggleIsOnlyTodayDelivery] = useToggle(false);

  const selection = useSelectionIds<string>([]);
  const table = useDataTableRequest<
    PaginationRequestParams<PartnerCompanyItemFilterV2Base>,
    DataPaginateResponse<PartnerCompanyItem[]>,
    ErrorResponse
  >({
    request: searchPartnerItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
    isValidToRequest: !showTracking,
    transformResponse: (res) => {
      const response = res.data as DataPaginateResponse<PartnerCompanyItem[]>;

      if (response.data.results) {
        sortOrderPendingByExpirationDate(response.data.results);

        if (
          response.data.pagination.offset === DEFAULT_PAGINATION.page &&
          response.data.results.length > 0
        ) {
          const selectedIds = response.data.results.map(
            (item) => item[ITEM_KEY_ID],
          );

          selection.utils.reset();
          selection.utils.addMultiple(selectedIds);
        }
      }

      return response;
    },
  });

  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      selection.utils.reset();

      table.handleChangeTable('filter', {
        pagination: {
          ...table.queryParams.pagination,
          page: 1,
        },
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [forceRequest, parseFiltersRequest, selection.utils, table],
  );

  useEffect(() => {
    if (forceRequest.value) {
      toggleShowTracking(false);
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    selection.utils.reset();

    table.reloadRequest({
      pagination: table.queryParams.pagination,
      filters: parseFiltersRequest(),
    });
  }, [selection.utils, table, parseFiltersRequest]);

  useInterval(() => {
    reloadZoneSaturation({
      withLoading: false,
      onlyTodayDelivery: isOnlyTodayDelivery,
    });
  }, DEFAULT_INTERVAL_RELOAD_ZONE_SATURATION);

  useClientOnMethod(
    hubConnection,
    METHODS_NAME.REFRESH_CARRIER_RECEIVE_ORDERS,
    () => handleForceRequests(),
  );

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentCollectTabDialogDetailsOrderData>
    >(null);

  const [
    openConfirmValidateOrder,
    selectedValidateOrder,
    handleOpenConfirmValidateOrder,
    handleCloseConfirmValidateOrder,
  ] = useToggleDialogConfirmation<Nullable<PartnerCompanyItem>>(null);

  const [
    openDialogCancelOrder,
    selectedCancelOrder,
    handleOpenDialogCancelOrder,
    handleCloseDialogCancelOrder,
  ] = useToggleDialogConfirmation<Nullable<PartnerCompanyItem>>(null);

  return (
    <DashboardVariantV2CarrierContentCollectTabContext.Provider
      value={{
        parseFiltersRequest,
        selection,
        table,
        showTracking: {
          value: showTracking,
          toggleValue: toggleShowTracking,
        },
        isOnlyTodayDelivery: {
          value: isOnlyTodayDelivery,
          toggleValue: toggleIsOnlyTodayDelivery,
        },
        actions: {
          reloadDataAndResetSelectedRows,
          handleForceRequests,
        },
        dialogs: {
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
          confirmValidateOrder: {
            open: openConfirmValidateOrder,
            data: selectedValidateOrder,
            handleOpen: handleOpenConfirmValidateOrder,
            handleClose: handleCloseConfirmValidateOrder,
          },
          cancelOrder: {
            open: openDialogCancelOrder,
            data: selectedCancelOrder,
            handleOpen: handleOpenDialogCancelOrder,
            handleClose: handleCloseDialogCancelOrder,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContentCollectTabContext.Provider>
  );
};

export default DashboardVariantV2CarrierContentCollectTabProvider;
